import { Grid, Typography } from "@mui/material";
import { GridColumns } from "@mui/x-data-grid";
import React from "react";
import SplitEmployeeDocumentAction from "./SplitEmployeeDocumentAction";
import { LegalEntityQuickfilter, useLegalEntityQuickFilter } from "../Quickfilters/LegalEntityQuickfilter";
import PublishSplitEmployeeDocument from "./PublishSplitEmployeeDocument/PublishSplitEmployeeDocument";
import SplitEmployeeDocumentFiles from "./SplitEmployeeDocumentFiles/SplitEmployeeDocumentFiles";
import useAuthorization from "../../custom-hooks/useAuthorization/useAuthorization";
import { CustomDate } from "../../models/custom-date/custom-date";
import { SplitEmployeeDocumentFilter } from "../../models/split-document/split-employee-document/split-employee-document-filter";
import ConcatenatedCell from "../../reusable-components/ConcatenatedCell/ConcatenatedCell";
import { NotAuthorizedPage } from "../../reusable-components/NotAuthorizedPage/NotAuthorizedPage";
import Table from "../../reusable-components/Table/Table";
import { SplitEmployeeDocumentApiService } from "../../services/split-document/split-employee-document/split-employee-document-api.service";
import { ACCESS_TYPES, AUTHORIZATIONS } from "../../utils/constants/authorization-constants";
import { intlFormatDateShort } from "../../utils/time-utils";
import AttachedDocuments from "../AttachedDocuments/AttachedDocuments";
const SplitEmployeeDocumentTable = () => {
  const ADMIN_ACL = [ACCESS_TYPES.CHANGE_ALL.id, ACCESS_TYPES.VIEW_ALL.id];
  const isAdmin = useAuthorization([...ADMIN_ACL]);

  const LE_ACL = [
    AUTHORIZATIONS.LegalEntityAccess.LegalEntityConfig,
    AUTHORIZATIONS.LegalEntityAccess.ProcessAccounting,
    AUTHORIZATIONS.LegalEntityAccess.RunAccountingReports,
    AUTHORIZATIONS.LegalEntityAccess.ViewComplianceData,
    AUTHORIZATIONS.LegalEntityAccess.ProcessIncomingDocuments,
    AUTHORIZATIONS.LegalEntityAccess.ProcessPayroll,
  ];
  const hasLEAccess = useAuthorization([...LE_ACL]);

  const hasAccess = isAdmin || hasLEAccess;

  const tRP = "components.upload_and_split_employee_pdf.table";

  const columns: GridColumns = [
    {
      field: "legalEntity",
      headerAlign: "center",
      renderHeader: () => <Typography>Legal entity</Typography>,
      renderCell: ({ row }) => (
        <Typography>{row.legalEntity.code ? row.legalEntity.code : row.legalEntity.name}</Typography>
      ),
    },
    {
      field: "originalDocument",
      headerAlign: "center",
      renderHeader: () => <Typography>Original file</Typography>,
      align: "center",
      renderCell: ({ row }) =>
        row.originalDocument ? (
          <AttachedDocuments
            count={row.originalDocument.filesCount}
            documentIDs={[row.originalDocument.id]}
            buttonSize="2x"
            maxFilesNumber={1}
            useSimpleView={true}
          />
        ) : (
          <></>
        ),
    },
    {
      field: "splitOn",
      headerAlign: "center",
      renderHeader: () => <Typography>Last split date</Typography>,
      flex: 0.5,
      renderCell: ({ row }) => (
        <Typography>
          {CustomDate.ToDate(row.splitOn) ? intlFormatDateShort(CustomDate.ToDate(row.splitOn)!) : ""}
        </Typography>
      ),
    },
    {
      field: "splitBy",
      headerAlign: "center",
      renderHeader: () => <Typography>Split by</Typography>,
      flex: 0.5,
      renderCell: ({ row }) => <Typography>{row.splitBy?.name}</Typography>,
    },
    {
      field: "",
      headerAlign: "center",
      renderHeader: () => <Typography>Files</Typography>,
      align: "center",
      renderCell: ({ row }) => <SplitEmployeeDocumentFiles item={row} />,
    },
    {
      field: "documentTypeUsedToPublish",
      headerAlign: "center",
      renderHeader: () => <Typography>Document type used to publish</Typography>,
      flex: 0.9,
      renderCell: ({ row }) =>
        row.documentTypeUsedToPublish ? (
          <ConcatenatedCell
            aligned="left"
            FirstElement={<Typography>{row.documentTypeUsedToPublish?.name}</Typography>}
            SecondElement={<Typography>{row.documentTypeUsedToPublish?.category?.name}</Typography>}
          />
        ) : (
          <></>
        ),
    },
    {
      field: "publishedOn",
      headerAlign: "center",
      renderHeader: () => <Typography>Published on</Typography>,
      flex: 0.5,
      renderCell: ({ row }) =>
        CustomDate.ToDate(row.publishedOn) ? (
          <Typography style={{ whiteSpace: "normal", lineHeight: "1rem" }}>{`Published on ${intlFormatDateShort(
            CustomDate.ToDate(row.publishedOn)!
          )} by ${row.publishedBy?.name}`}</Typography>
        ) : (
          ""
        ),
    },
  ];

  const {
    legalEntitiesQuickFilter,
    setLegalEntitiesQuickFilter,
    selectedLegalEntity,
    legalEntityQFIsLoading,
    allLegalEntities,
  } = useLegalEntityQuickFilter(true);

  const AdditionalActions = (props) => {
    return (
      <Grid
        style={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <SplitEmployeeDocumentFiles {...props} />
        <PublishSplitEmployeeDocument {...props} />
      </Grid>
    );
  };

  return (
    <>
      {hasAccess ? (
        !legalEntityQFIsLoading && (
          <Table
            columns={columns}
            translationRootPath={tRP}
            serviceApi={new SplitEmployeeDocumentApiService()}
            filter=""
            addEditType="custom"
            CustomButtonToRender={SplitEmployeeDocumentAction}
            propsForCustomButtonToRender={{
              legalEntity: selectedLegalEntity,
            }}
            propsFilter={
              {
                legalEntityIds: legalEntitiesQuickFilter.legalEntities.legalEntityIds,
              } as SplitEmployeeDocumentFilter
            }
            quickFilters={
              <LegalEntityQuickfilter
                quickFilters={legalEntitiesQuickFilter}
                handleChangeQuickFilters={setLegalEntitiesQuickFilter}
                allLegalEntitiesOption
                allLegalEntities={allLegalEntities}
                {...(!isAdmin && {
                  authorizations: [...LE_ACL],
                })}
              />
            }
            AdditionalActionsforAddEditColumn={AdditionalActions}
            propsForAdditionalActionsforAddEditColumn={{
              legalEntity: selectedLegalEntity,
              buttonType: "text",
              resplit: true,
            }}
            checkIsRowDeletable={(row) => {
              return !row.publishedOn;
            }}
          />
        )
      ) : (
        <NotAuthorizedPage />
      )}
    </>
  );
};
export default SplitEmployeeDocumentTable;
